import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { bzIconCheck, bzIconCopy } from '@bringoz/ui/icons';
import { BzIcon } from '../icon/bz-icon';

/**
 * Copies `text` to the clipboard and swaps its icon to a checkmark for a
 * couple of seconds so the click visibly did something.
 *
 * The legacy `app-text-copy-button` confirmed a copy through
 * `bringozToastService` — a dependency this library does not have (`bz-toast`
 * is blocked on the `@angular/cdk` question). Confirming in place instead
 * removes the dependency entirely; a consumer that wants a toast too can
 * still fire one from the `copied` output.
 *
 * `copy` and `check` are bound directly rather than looked up by name: they
 * are always exactly these two icons, never a runtime choice, so this is the
 * `[icon]` case the icon component's own docs recommend — no
 * `provideBzIcons()` registration required to make this component work.
 */
@Component({
  selector: 'bz-copy-button',
  imports: [BzIcon],
  templateUrl: './bz-copy-button.html',
  styleUrl: './bz-copy-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzCopyButton {
  readonly text = input.required<string>();
  readonly label = input('Copy to clipboard');

  readonly copied = output<void>();

  protected readonly copyIcon = bzIconCopy;
  protected readonly checkIcon = bzIconCheck;
  protected readonly justCopied = signal(false);

  protected copy(): void {
    void navigator.clipboard.writeText(this.text()).then(() => {
      this.justCopied.set(true);
      this.copied.emit();
      setTimeout(() => this.justCopied.set(false), 2000);
    });
  }
}
